import { RefObject, useCallback, useContext, useEffect, useState } from 'react';
import { Box, Button, Popover, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import dateFormat from 'date-fns-tz/format';
import { useTranslation } from 'react-i18next';
import DoneIcon from '@mui/icons-material/Done';
import { MoreHoriz } from '@mui/icons-material';
import styled from 'styled-components';
import { useHistory, useParams } from 'react-router-dom';
import { addSeconds } from 'date-fns';
import { copyToClipboard } from '../../utils/clipboard';
import { getUrls } from '../../utils/url';
import { ClassRecording, Permission, RecordingDialogProps } from './types';
import { ActionMenu, ActionMenuItem } from '../common/table/ActionMenu';
import { DeleteRecordingDialog } from './DeleteRecordingDialog';
import { EditRecordingDialog } from './EditRecordingDialog';
import { RecordingLinksDialog } from './RecordingLinksDialog';
import Loadable from '../common/loadable';
import { ClassContext } from '../../context/ClassContext';
import { NoRecords } from './SharedComponents';
import { ViewAnalyticsDialog } from './ViewAnalyticsDialog';
import { useSchoolInfo } from '../../hooks/useSchoolInfo';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { getRecordingInfo } from './util';
import { RecordingStatus } from '../../types/recordingTypes';
import { useTransmitUserDetails } from '../../hooks/useTransmission';

interface RecordingsTableProps {
    recordings: ClassRecording[];
    setRecordings: (recordings: ClassRecording[]) => void;
    isLoading: boolean;
    isInstructor: boolean;
    isGuest: boolean;
}

const StyledPopover = styled(Popover)`
    .MuiPopover-paper {
        background-color: #616161 !important;
        color: #ffffff !important;
    }
`;

const ActionsBox = styled(Box)`
    display: flex;
    flex-direction: row;

    & > * {
        margin-right: 6px;
    }
`;

const TableCellTime = styled(TableCell)`
    width: 20%;
`;

const TableCellName = styled(TableCell)`
    width: 50%;
`;

const TableCellDuration = styled(TableCell)`
    width: 15%;
`;

const TableCellStatus = styled(TableCell)`
    width: 15%;
`;

const TableCellAction = styled(TableCell)`
    width: auto;
`;

const TableCellNoWrap = styled(TableCell)`
    white-space: nowrap;
`;

export const RecordingsTable = ({
    recordings,
    setRecordings,
    isInstructor,
    isLoading,
    isGuest
}: RecordingsTableProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const {
        classUuid,
        classData: { class: classInfo },
        userUuid
    } = useContext(ClassContext);

    const { data: orgSettings } = useOrgSettings();

    const { data: schoolInfo } = useSchoolInfo();
    const { transmitUserDetails } = useTransmitUserDetails();

    const { recordingUuid, action } = useParams<{ recordingUuid?: string; action?: string }>();
    const [analyticsDialogOpen, setAnalyticsDialogOpen] = useState<boolean>(!!recordingUuid && action === 'analytics');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [linksDialogOpen, setLinksDialogOpen] = useState(false);
    const [popoverCopyLinkOpen, setPopoverCopyLinkOpen] = useState(false);
    const [popoverAnchorEl, setPopoverAnchorEl] = useState<RefObject<unknown> | null>(null);

    const handleAnalyticsDialogClosed = () => {
        setAnalyticsDialogOpen(false);
        history.push(`/lti/${classUuid}${window.location.search}`);
    };

    useEffect(() => {
        setAnalyticsDialogOpen(!!recordingUuid && action === 'analytics');
    }, [recordingUuid, action]);

    const initDialogProps: RecordingDialogProps = {
        open: false,
        downloading: false,
        recording: undefined,
        recordings,
        setRecordings,
        handleDialogClosed: () => {}
    };

    const [deleteDialogProps, setDeleteDialogProps] = useState<RecordingDialogProps>({
        ...initDialogProps,
        handleDialogClosed: () => setDeleteDialogOpen(false)
    });

    const [linksDialogProps, setLinksDialogProps] = useState<RecordingDialogProps>({
        ...initDialogProps,
        handleDialogClosed: () => setLinksDialogOpen(false)
    });

    const [editDialogProps, setEditDialogProps] = useState<RecordingDialogProps>({
        ...initDialogProps,
        handleDialogClosed: () => setEditDialogOpen(false)
    });

    const handleActionRefresh = useCallback(
        async (recordingId: string) => {
            const info = await getRecordingInfo(schoolInfo!.uuid!, userUuid, classUuid, recordingId, false);
            if (info) {
                console.log('Recording refreshed');
                setRecordings(
                    recordings.map((rec) =>
                        rec.recordingId === recordingId
                            ? { ...rec, status: info.status, duration: info.duration ?? 0 }
                            : rec
                    )
                );
            }
        },
        [schoolInfo, userUuid, classUuid, recordings, setRecordings]
    );

    const getRecordingLinkForRecording = useCallback(
        (recording: ClassRecording): string => {
            const url = new URL(
                `${getUrls().vanityUrl}/player/recording/${classInfo.class_uuid}/${recording.recordingId}`
            );
            return url.toString();
        },
        [classInfo]
    );

    const handleCopyLink = useCallback(
        async (uniqueId: string, refEl: RefObject<unknown>) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            const url = getRecordingLinkForRecording(recording!);
            await copyToClipboard(url);
            setPopoverAnchorEl(refEl);
            setPopoverCopyLinkOpen(true);
            // Auto close popover after 2 seconds.
            window.setTimeout(() => {
                setPopoverCopyLinkOpen(false);
            }, 2000);
        },
        [recordings, getRecordingLinkForRecording]
    );

    const handleLaunch = useCallback(
        (recording: ClassRecording) => {
            transmitUserDetails(new URL(getRecordingLinkForRecording(recording)));
        },
        [getRecordingLinkForRecording, transmitUserDetails]
    );

    const handleExternalLink = useCallback(
        (uniqueId: string) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            setLinksDialogProps({ ...linksDialogProps, downloading: false, recording });
            setLinksDialogOpen(true);
        },
        [recordings, linksDialogProps]
    );

    const handleActionEdit = useCallback(
        (uniqueId: string) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            setEditDialogProps({ ...editDialogProps, recording });
            setEditDialogOpen(true);
        },
        [recordings, editDialogProps]
    );

    const handleActionDownload = useCallback(
        (uniqueId: string) => {
            history.push(`/lti/${classUuid}/recording/${uniqueId}/downloads${window.location.search}`);
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            setLinksDialogProps({ ...linksDialogProps, downloading: true, recording });
            setLinksDialogOpen(true);
        },
        [recordings, linksDialogProps, classUuid, history]
    );

    const handleActionDelete = useCallback(
        (uniqueId: string) => {
            const recording = recordings.find((r) => r.recordingId === uniqueId);
            setDeleteDialogProps({ ...deleteDialogProps, recording });
            setDeleteDialogOpen(true);
        },
        [recordings, deleteDialogProps]
    );

    const handleActionViewAnalytics = useCallback(
        (uniqueId: string) => {
            history.push(`/lti/${classUuid}/recording/${uniqueId}/analytics${window.location.search}`);
            setAnalyticsDialogOpen(true);
        },
        [classUuid, history]
    );

    function getActions(recording: ClassRecording): ActionMenuItem[] {
        const items: ActionMenuItem[] = [{ name: t('common:refresh'), onClick: handleActionRefresh }];

        if (isInstructor) {
            items.push({ name: t('common:edit'), onClick: handleActionEdit });
        }

        if (recording.status === RecordingStatus.EXTERNAL) {
            items.push({ name: t('lti:recordings.external-link'), onClick: handleExternalLink });

            if (isInstructor) {
                items.push({ name: t('common:delete'), onClick: handleActionDelete });
            }
        } else if (recording.status === RecordingStatus.AVAILABLE) {
            items.push({ name: t('lti:recordings.copy-link'), onClick: handleCopyLink });
            if (isInstructor || orgSettings?.recordingSettings.participantsCanDownload) {
                items.push({ name: t('common:download'), onClick: handleActionDownload });
            }

            if (isInstructor) {
                items.push({ name: t('lti:recordings.view-analytics'), onClick: handleActionViewAnalytics });
                items.push({ name: t('common:delete'), onClick: handleActionDelete });
            }
        }

        return items;
    }

    const recordingTimes = (recording: ClassRecording) => {
        const hasDuration = recording.duration && Number(recording.duration) > 0;
        const startDate = new Date(recording.when);
        const endDate = hasDuration ? addSeconds(new Date(recording.when), recording.duration) : null;
        let startDateStr = dateFormat(startDate, 'PPPP');
        let endDateStr = endDate ? dateFormat(endDate, 'PPPP') : null;
        let date = startDateStr;
        if (startDateStr !== endDateStr && endDate !== null) {
            // The recording spans two dates!
            // Use short date formats for range.
            startDateStr = dateFormat(startDate, 'P');
            endDateStr = dateFormat(endDate, 'P');
            date = `${startDateStr} - ${endDateStr}`;
        }
        const endTimeStr = endDate !== null ? dateFormat(endDate, 'p') : null;
        let times;
        if (endTimeStr) {
            times = `${dateFormat(startDate, 'p')} - ${endTimeStr}`;
        } else {
            // No valid duration.
            times = `${dateFormat(startDate, 'p')}`;
        }
        return (
            <>
                <Box>{date}</Box>
                <Box>{times}</Box>
            </>
        );
    };

    const recordingDuration = (recording: ClassRecording) => {
        if (!recording.duration) {
            return null;
        }
        // https://codingbeautydev.com/blog/javascript-convert-seconds-to-hours-and-minutes/

        // Note - we are calculating hours, minutes and seconds this way instead of using dateFns because it is
        // possible that a recording could go beyond 24 hours.
        const totalSeconds = recording.duration;
        const totalMinutes = Math.floor(totalSeconds / 60);
        const seconds = (totalSeconds % 60).toFixed(0);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const secondsStr = `${seconds}`.padStart(2, '0');
        const hoursStr = `${hours}`.padStart(2, '0');
        const minutesStr = `${minutes}`.padStart(2, '0');

        return (
            <Box>
                {hoursStr}:{minutesStr}:{secondsStr}
            </Box>
        );
    };

    const permissionsString = (permission: Permission) => t(`lti:recordings.permissions.${permission.toLowerCase()}`);

    return (
        <Loadable fullScreen={false} isLoading={isLoading}>
            <ViewAnalyticsDialog
                {...{ open: analyticsDialogOpen, handleClose: handleAnalyticsDialogClosed, recordingUuid }}
            />
            <DeleteRecordingDialog {...{ ...deleteDialogProps, open: deleteDialogOpen, recordings }} />
            <EditRecordingDialog {...{ ...editDialogProps, open: editDialogOpen, recordings }} />
            <RecordingLinksDialog {...{ ...linksDialogProps, open: linksDialogOpen, recordings }} />
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCellTime>{t('class:time')}</TableCellTime>
                        <TableCellName>{t('class:name')}</TableCellName>
                        <TableCellDuration>{t('class:duration')}</TableCellDuration>
                        <TableCellStatus>{t('class:status')}</TableCellStatus>
                        <TableCellAction>{t('class:actions')}</TableCellAction>
                    </TableRow>
                    {recordings &&
                        recordings.map((recording) => (
                            <TableRow key={recording.recordingId}>
                                <TableCellNoWrap>{recordingTimes(recording)}</TableCellNoWrap>
                                <TableCell>{recording.name}</TableCell>
                                <TableCellNoWrap>{recordingDuration(recording)}</TableCellNoWrap>
                                <TableCell>{permissionsString(recording.permission)}</TableCell>
                                <TableCell>
                                    <ActionsBox>
                                        <ActionMenu
                                            uniqueId={recording.recordingId}
                                            title={t('recordingAction')}
                                            actions={getActions(recording)}
                                            sx={{ py: '2px', px: '3px' }}
                                        >
                                            <MoreHoriz color="primary" fontSize="large" />
                                        </ActionMenu>
                                        {!isGuest && recording.status === 'AVAILABLE' ? (
                                            <Button
                                                sx={{ minWidth: '140px' }}
                                                color="primary"
                                                variant="contained"
                                                onClick={() => handleLaunch(recording)}
                                            >
                                                {t('lti:recordings.launch')}
                                            </Button>
                                        ) : (
                                            <Box sx={{ width: '100%', textAlign: 'center', fontSize: '14px' }}>
                                                {t(`lti:recordings.statusShort.${recording.status.toLowerCase()}`)}
                                            </Box>
                                        )}
                                    </ActionsBox>
                                </TableCell>
                            </TableRow>
                        ))}
                </TableHead>
            </Table>
            {recordings && !recordings.length && <NoRecords message={t('lti:recordings.noneformeeting')} />}
            <StyledPopover
                id="copy-link"
                open={popoverCopyLinkOpen}
                anchorEl={popoverAnchorEl?.current as HTMLElement}
                onClose={() => setPopoverCopyLinkOpen(false)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                <Typography sx={{ py: 1, px: 2, display: 'flex', alignItems: 'center' }}>
                    {t('common:copiedlink')}
                    <DoneIcon sx={{ color: '#fff', marginLeft: '5px' }} />
                </Typography>
            </StyledPopover>
        </Loadable>
    );
};
